import styled from "styled-components";
import { fluid } from "@/styles/utils/fluid";
import { PrimaryButton } from "@/styles/Styled.Buttons";
import {
  TypoBodyStyles,
  TypoCaptionStyles,
  TypoLabelStyles,
  TypoOverline2,
} from "@/styles/Styled.Typography";

export const FormCard = styled.div`
  display: flex;
  position: relative;
  flex-direction: column;
  border-radius: 32px;
  border: 4px solid ${({ theme }) => theme.colors.onyx};
  &::before {
    content: "";
    background-color: ${({ theme }) => theme.colors.white};
    outline: 4px solid ${({ theme }) => theme.colors.white};
    position: absolute;
    border-radius: 24px;
    top: 4px;
    left: 4px;
    right: 4px;
    bottom: 4px;
    z-index: -1;
    background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='24' ry='24' stroke='%23333' stroke-width='2' stroke-dasharray='8%2c 8' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e");
  }
  legend {
    position: absolute;
    top: -16px;
    left: 50%;
    z-index: 10;
    transform: translateX(-50%) rotate(-4deg);
  }
  fieldset {
    border: 0;
    padding: 0;
    width: 100%;
  }
  small {
    width: 100%;
    text-align: center;
  }
  p {
    &:last-child {
      margin-bottom: 0;
    }
  }
  h3 {
    position: absolute;
    top: -16px;
    left: 50%;
    transform: translateX(-50%) rotate(-4deg);
  }
  padding: ${({ theme }) =>
    fluid({
      minSize: theme.spacing.card.xs,
      maxSize: theme.spacing.card.xl,
      minVW: theme.breakpointValues.xs,
      maxVW: theme.breakpointValues.xl,
      baseFontMin: theme.baseFont.xs,
      baseFontMax: theme.baseFont.xl,
    })};
`;

export const FormInputText = styled.p`
  ${TypoBodyStyles}
  color: ${({ theme }) => theme.colors.phillipineSilver};
  background-color: ${({ theme }) => theme.colors.white};
  text-transform: capitalize;
`;

export const FormButton = styled(PrimaryButton)`
  width: 100%;
  @media ${({ theme }) => theme.breakpoints.md} {
    width: auto;
  }
  @media ${({ theme }) => theme.breakpoints.xl} {
    width: auto;
  }
`;

export const TextFieldWrapper = styled.div`
  position: relative;
  input {
    ${TypoBodyStyles}
    width: 100%;
    height: 100%;
    border: 1px solid ${({ theme }) => theme.colors.onyx};
    border-radius: 9999em;
    box-sizing: border-box;
    line-height: ${({ theme }) => theme.lineHeight.button}rem;
    padding: ${({ theme }) => `
      calc(${fluid({
        minSize: theme.spacing.formLabel.xs,
        maxSize: theme.spacing.formLabel.xl,
        minVW: theme.breakpointValues.xs,
        maxVW: theme.breakpointValues.xl,
        baseFontMin: theme.baseFont.xs,
        baseFontMax: theme.baseFont.xl,
      })} / 2.5)
      ${fluid({
        minSize: theme.spacing.formLabel.xs,
        maxSize: theme.spacing.formLabel.xl,
        minVW: theme.breakpointValues.xs,
        maxVW: theme.breakpointValues.xl,
        baseFontMin: theme.baseFont.xs,
        baseFontMax: theme.baseFont.xl,
      })}
    `};
    &:focus {
      outline: none;
      border: 1px solid ${({ theme }) => theme.colors.imperialRed};
    }
  }
  label {
    position: absolute;
    top: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    pointer-events: none;
    left: ${({ theme }) =>
      fluid({
        minSize: theme.spacing.formLabel.xs,
        maxSize: theme.spacing.formLabel.xl,
        minVW: theme.breakpointValues.xs,
        maxVW: theme.breakpointValues.xl,
        baseFontMin: theme.baseFont.xs,
        baseFontMax: theme.baseFont.xl,
      })};
  }
`;

export const MessageContainer = styled.div`
  padding-top: ${({ theme }) => theme.fontScale.majorSecond.size}rem;
  display: flex;
  width: 100%;
`;

export const FormMessageStyle = styled.div`
  ${TypoCaptionStyles};
  font-family: ${({ theme }) => theme.fontFace.maitree};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.emerald};
`;

export const ModulePin = styled(TypoOverline2)`
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.onyx};
  padding: 8px;
  border-radius: 8px;
  width: min-content;
  white-space: nowrap;
`;

export const GridFormWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-auto-rows: min-content;
  min-height: 0;
  min-width: 0; /* NEW; needed for Firefox */
  &:last-of-type {
    border-bottom: none;
  }
  gap: ${({ theme }) => `
    ${fluid({
      minSize: theme.spacing.formGap.xs,
      maxSize: theme.spacing.formGap.xl,
      minVW: theme.breakpointValues.xs,
      maxVW: theme.breakpointValues.xl,
      baseFontMin: theme.baseFont.xs,
      baseFontMax: theme.baseFont.xl,
    })}
    0
  `};
`;

export const GridItemFormWrapper = styled.div`
  padding: 0;
  grid-row: auto;
  &:nth-of-type(2) {
    justify-self: center;
    width: 90%;
  }
  small {
    ${TypoCaptionStyles}
    font-weight: ${({ theme }) => theme.fontWeight.regular};
    width: 100%;
    display: flex;
  }
`;

export const GridForm = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-auto-rows: min-content;
  min-height: 0;
  min-width: 0; /* NEW; needed for Firefox */
  &:last-of-type {
    border-bottom: none;
  }
  gap: ${({ theme }) => `
    ${fluid({
      minSize: theme.spacing.formGap.xs,
      maxSize: theme.spacing.formGap.xl,
      minVW: theme.breakpointValues.xs,
      maxVW: theme.breakpointValues.xl,
      baseFontMin: theme.baseFont.xs,
      baseFontMax: theme.baseFont.xl,
    })}
    0
  `};
  grid-template-areas:
    "title"
    "description"
    "input"
    "button";
  /* md and up */
  @media ${({ theme }) => theme.breakpoints.lg},
    /* lg to xl */ ${({ theme }) => theme.breakpoints.xl} {
    /* If $cols contains the 2-column flag, use a two column layout; otherwise stay single column */
    grid-template-columns: ${({ $cols = [] }) =>
      $cols.includes("$gForm2Col") ? "1fr auto" : "1fr"};
    grid-template-areas: ${({ $cols = [] }) =>
      $cols.includes("$gForm2Col")
        ? `"title title"
           "description description"
           "input button"`
        : `"title"
           "description"
           "input"
           "button"`};
    gap: ${({ theme }) =>
      fluid({
        minSize: theme.spacing.formGap.xs,
        maxSize: theme.spacing.formGap.xl,
        minVW: theme.breakpointValues.xs,
        maxVW: theme.breakpointValues.xl,
        baseFontMin: theme.baseFont.xs,
        baseFontMax: theme.baseFont.xl,
      })};
  }
`;

export const GridItemForm = styled.div`
  grid-column: ${({ $spanFull }) => ($spanFull ? "1 / -1" : "auto")};
  grid-area: ${({ area }) => area || "auto"}; // add area prop
  min-width: 0;
  width: 100%;
`;

export const FormTitle = styled.div`
  ${TypoLabelStyles};
  font-family: ${({ theme }) => theme.fontFace.itc_avant_garde_gothicbold};
  text-transform: capitalize;
  padding-top: ${({ theme }) =>
    fluid({
      minSize: theme.spacing.formGap.xs,
      maxSize: theme.spacing.formGap.xl,
      minVW: theme.breakpointValues.xs,
      maxVW: theme.breakpointValues.xl,
      baseFontMin: theme.baseFont.xs,
      baseFontMax: theme.baseFont.xl,
    })};
`;
